import React, { useMemo } from 'react';
import { useChatAgentPermitted } from 'components/ChatAgent/hooks/useChatAgentPermitted';
import { ENTITY } from 'modules/campaigns/config';
import {
    FEATURE_FLAGS,
    useAccount,
    useConfigMatch,
} from 'modules/taboola-common-frontend-modules/account-configurations';
import { usePermissions } from 'modules/taboola-common-frontend-modules/authentication';
import { isNetworkAccountType } from '../../../../../../account-management';
import { isCreativeResubmittable } from '../../../config/predicates';
import { useIsCreativeResubmitEnabled } from '../../../hooks/useCreativeResubmit';
import { useReportComfortableViewParam } from '../../../hooks/useReportComfortableViewParam';
import { FixWithAiButton } from './FixWithAiButton';
import { ResubmitButton } from './ResubmitButton';
import styles from './crtButtons.module.scss';

const DESCRIPTION_REASON_PREFIX = 'DESCRIPTION_';

export const CRTButtons = ({ data, entityType, isRejected }) => {
    const { id, craPolicyViolation, title, description, url, rejectReason } = data;
    const descriptionRejectionsEnabled = useConfigMatch({
        [FEATURE_FLAGS.CHAT_AGENT_FIX_WITH_AI_BUTTON_DESCRIPTION_REJECTION_ENABLED]: 'true',
    });
    const mismatchedRejectionsEnabled = useConfigMatch({
        [FEATURE_FLAGS.CHAT_AGENT_FIX_WITH_AI_BUTTON_MISMATCHED_REJECTION_ENABLED]: 'true',
    });
    const [isComfortableViewEnabled] = useReportComfortableViewParam();
    const isCreativeResubmissionEnabled = useIsCreativeResubmitEnabled();
    const isFixWithAIButtonEnabled = useConfigMatch({
        [FEATURE_FLAGS.CHAT_AGENT_FIX_WITH_AI_BUTTON_ENABLED]: 'true',
    });
    const isChatAgentPermitted = useChatAgentPermitted();

    const account = useAccount();
    const isNetworkAccount = isNetworkAccountType(account.type);
    const creativeEditEnabled = usePermissions('CREATIVE_EDIT');

    const resubmitButton = isCreativeResubmissionEnabled && entityType === ENTITY.CREATIVE && (
        <ResubmitButton creativeId={id} />
    );
    const hasValidCRAPrediction = useMemo(() => {
        // prediction is valid if there is a prediction AND
        // there's a reject reason AND
        // the mismatched FF is enabled [or] the prediction contains the reject reason AND
        // the description FF is enabled [or] the prediction has no entries w/ description prefix
        return (
            craPolicyViolation?.prediction &&
            rejectReason &&
            (mismatchedRejectionsEnabled || craPolicyViolation.prediction.includes(rejectReason)) &&
            (descriptionRejectionsEnabled ||
                !craPolicyViolation.prediction.split(',').some(reason => reason.startsWith(DESCRIPTION_REASON_PREFIX)))
        );
    }, [craPolicyViolation, descriptionRejectionsEnabled, mismatchedRejectionsEnabled, rejectReason]);

    const craViolationMatchesCreative =
        craPolicyViolation &&
        craPolicyViolation.title === title &&
        craPolicyViolation.description === description &&
        craPolicyViolation.url === url;
    const fixButton = isRejected &&
        creativeEditEnabled &&
        isChatAgentPermitted &&
        entityType === ENTITY.CREATIVE &&
        isFixWithAIButtonEnabled &&
        (!isCreativeResubmissionEnabled || isCreativeResubmittable(data)) &&
        hasValidCRAPrediction &&
        craViolationMatchesCreative &&
        !isNetworkAccount && <FixWithAiButton craPolicyViolation={craPolicyViolation} data={data} />;

    if (!resubmitButton && !fixButton) {
        return null;
    }

    return isComfortableViewEnabled ? (
        <>
            {fixButton}
            {resubmitButton}
        </>
    ) : (
        <div className={styles['container']}>
            {fixButton}
            {resubmitButton}
        </div>
    );
};
